import { loadCalculatorRuntimeModule } from "../calculator-runtime-loader.js";
import { parseCalculatorUserOverlayDocumentV4 } from "../calculator-user-layer-v4.js";
import {
  CALCULATOR_ENGINE_STATUSES,
  parseCalculatorEngineInitialModelDocumentV2,
  parseCalculatorEngineModelResponseV2,
  parseCalculatorEngineScenarioV2,
  parseCalculatorEngineValidationResponseV1,
} from "./calculator-engine-contract.js";
import { parseCalculatorInspectorProjectionV1 } from "./calculator-layer-inspector-contract.js";
import { expandCalculatorCompactModelBundleV1 } from "./calculator-compact-model.js";
import {
  assertCalculatorProjectionOperationBinding,
  buildCalculatorProjectionOperationIndex,
  hydrateCalculatorProjectionTraceCandidates,
} from "./calculator-projection-operation-index.js";
import {
  CALCULATOR_ENGINE_CONTROL_REVISION_SELECTOR,
  parseCalculatorControlRevision,
  parseCalculatorControlRevisionTemplate,
  prepareCalculatorControlRevision,
} from "./calculator-control-revision.js";
import { createCalculatorPresentationRevision } from "./calculator-presentation-revision.js";
import { createCalculatorModelRequestLifecycle } from "./calculator-model-request-lifecycle.js";

export { CALCULATOR_ENGINE_CONTROL_REVISION_SELECTOR } from "./calculator-control-revision.js";

export const CALCULATOR_ENGINE_TAG = "fishy-calculator-engine";
export const CALCULATOR_ENGINE_MODEL_DOCUMENT_SELECTOR =
  'script[type="application/json"][data-calculator-model-v2]';
export const CALCULATOR_ENGINE_PROJECTION_EVENT =
  "fishystuff:calculator-engine-projection";
export const CALCULATOR_ENGINE_STATUS_EVENT =
  "fishystuff:calculator-engine-status";
export const CALCULATOR_ENGINE_CONNECTED_EVENT =
  "fishystuff:calculator-engine-connected";
export const CALCULATOR_ENGINE_PRESENTATION_EVENT =
  "fishystuff:calculator-presentation";
export const CALCULATOR_ENGINE_MODEL_COMMITTED_EVENT =
  "fishystuff:calculator-model-committed";
export const CALCULATOR_ENGINE_MODEL_REVISION_ACTIVATED_EVENT =
  "fishystuff:calculator-model-revision-activated";
export const CALCULATOR_ENGINE_MODEL_REQUEST_EVENT =
  "fishystuff-calculator-model-request";
export const CALCULATOR_ENGINE_SCENARIO_INTENT_EVENT =
  "fishystuff:calculator-scenario-intent";
export const CALCULATOR_ENGINE_MODEL_RESPONSE_TAG =
  "fishy-calculator-model-response";
export const CALCULATOR_ENGINE_MODEL_RESPONSE_DOCUMENT_SELECTOR =
  'script[type="application/json"][data-calculator-model-response-v2]';
export const CALCULATOR_ENGINE_PRESENTATION_DOCUMENT_SELECTOR =
  'script[type="application/json"][data-calculator-presentation-v1]';
export const CALCULATOR_ENGINE_USER_LAYER_DOCUMENT_SELECTOR =
  'script[type="application/json"][data-calculator-user-layer-v4]';
export const CALCULATOR_ENGINE_SOURCE_REFERENCE_DOCUMENT_SELECTOR =
  'script[type="application/json"][data-calculator-source-reference-v1]';

const RESULT_SHARE_UNAVAILABLE_TOOLTIP =
  "Result sharing is unavailable while dependent outputs are non-current.";
const HTMLElementBase = globalThis.HTMLElement ?? class {};

function upgradeProperty(element, property) {
  if (!Object.prototype.hasOwnProperty.call(element, property)) {
    return;
  }
  const value = element[property];
  delete element[property];
  element[property] = value;
}

function eventWithDetail(type, detail, { cancelable = false } = {}) {
  if (typeof globalThis.CustomEvent === "function") {
    return new globalThis.CustomEvent(type, {
      bubbles: true,
      cancelable,
      composed: true,
      detail,
    });
  }
  const event = new Event(type, { bubbles: true, cancelable });
  Object.defineProperty(event, "detail", { value: detail });
  return event;
}

function requireRequestId(value, name = "requestId") {
  if (typeof value !== "string" || value.length === 0 || value.trim() !== value) {
    throw new TypeError(`${name} must be a non-empty identifier without surrounding whitespace`);
  }
  return value;
}

function serializedError(error) {
  return Object.freeze({
    name: error instanceof Error ? error.name : "Error",
    message: error instanceof Error ? error.message : String(error),
  });
}

function sameJson(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function isIntentPatchRecord(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function mergeScenarioIntentPatch(current, patch) {
  if (!isIntentPatchRecord(current) || !isIntentPatchRecord(patch)) {
    return patch;
  }
  const merged = { ...current };
  for (const [key, value] of Object.entries(patch)) {
    merged[key] = isIntentPatchRecord(value) && isIntentPatchRecord(merged[key])
      ? mergeScenarioIntentPatch(merged[key], value)
      : value;
  }
  return merged;
}

function outputIdKey(outputId) {
  return JSON.stringify(outputId);
}

function invalidatedOutputValidity(current, affectedOutputs) {
  const affected = new Set(affectedOutputs.map(outputIdKey));
  return Object.freeze(current.map((entry) => Object.freeze({
    ...entry,
    state: entry.state === "invalid" || affected.has(outputIdKey(entry.outputId))
      ? "invalid"
      : entry.state,
  })));
}

function staleOutputValidity(current) {
  return Object.freeze(current.map((entry) => Object.freeze({
    ...entry,
    state: entry.state === "current" ? "stale" : entry.state,
  })));
}

function statusLabel(status) {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function parseJsonDocument(document, name) {
  const encoded = document?.textContent?.trim();
  if (!encoded) {
    throw new Error(`${name} document is empty`);
  }
  try {
    return JSON.parse(encoded);
  } catch (error) {
    throw new Error(`${name} document is not valid JSON`, { cause: error });
  }
}

function parseStaticPresentation(value) {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError("calculator static presentation must be an object");
  }
  const fields = [
    "distributions",
    "formulas",
    "resources",
    "sankey",
    "schemaVersion",
    "session",
    "statTerms",
    "stats",
    "summaryMetrics",
    "target",
    "timeline",
    "values",
  ];
  const actual = Object.keys(value).sort();
  const expected = [...fields].sort();
  if (
    actual.length !== expected.length
    || actual.some((field, index) => field !== expected[index])
  ) {
    throw new TypeError(`calculator static presentation fields must be exactly: ${expected.join(", ")}`);
  }
  if (value.schemaVersion !== 1) {
    throw new TypeError("calculator static presentation schemaVersion must be 1");
  }
  return value;
}

function parseSourceReference(value) {
  if (
    value === null
    || typeof value !== "object"
    || Array.isArray(value)
    || Object.keys(value).length !== 1
    || typeof value.sourceReference !== "string"
    || value.sourceReference.trim() === ""
    || value.sourceReference.trim() !== value.sourceReference
  ) {
    throw new TypeError("calculator source reference document must contain only sourceReference");
  }
  return value.sourceReference;
}

export class FishyCalculatorEngine extends HTMLElementBase {
  constructor() {
    super();
    this._model = null;
    this._wireModel = null;
    this._engine = null;
    this._runtime = null;
    this._runtimePromise = null;
    this._runtimePromiseEngineVersion = null;
    this._runtimeLoader = loadCalculatorRuntimeModule;
    this._scenario = null;
    this._confirmedScenario = null;
    this._scenarioNeedsEvaluation = false;
    this._scenarioIntentTransaction = null;
    this._localIntentRevision = 0;
    this._userLayer = null;
    this._operationIndex = null;
    this._projection = null;
    this._confirmedProjection = null;
    this._presentation = null;
    this._outputValidity = Object.freeze([]);
    this._repairReport = Object.freeze({ repairs: Object.freeze([]) });
    this._status = "loading";
    this._statusMessage = "Loading calculator engine.";
    this._statusError = null;
    this._lastRequestId = null;
    this._connected = false;
    this._connectionRevision = 0;
    this._activationSequence = 0;
    this._pendingModelRequestId = null;
    this._pendingModelIntentRevision = null;
    this._modelRequestLifecycle = createCalculatorModelRequestLifecycle((requestId) => {
      this.failModelRequest(
        requestId,
        new Error("calculator model request finished without a response carrier"),
      );
    });
    this._pendingValidationRequestId = null;
    this._pendingValidationIntentRevision = null;
  }

  connectedCallback() {
    for (const property of ["runtimeLoader", "scenario", "userLayer"]) {
      upgradeProperty(this, property);
    }
    this._connected = true;
    const connectionRevision = ++this._connectionRevision;
    this._setStatus("loading", "Loading calculator engine.");

    // A defined custom element is connected when its opening tag is parsed,
    // before parser-inserted JSON children exist. One microtask keeps loading
    // visible while letting that same HTML insertion finish atomically.
    void Promise.resolve().then(() => {
      if (!this._connected || connectionRevision !== this._connectionRevision) {
        return;
      }
      this.dispatchEvent?.(eventWithDetail(
        CALCULATOR_ENGINE_CONNECTED_EVENT,
        Object.freeze({ schemaVersion: 1 }),
      ));
      let response;
      try {
        response = this._readEmbeddedInitialModelDocument();
        const userLayers = Array.from(
          this.querySelectorAll?.(CALCULATOR_ENGINE_USER_LAYER_DOCUMENT_SELECTOR) ?? [],
        );
        if (userLayers.length !== 1) {
          throw new Error("fishy-calculator-engine requires exactly one v4 user-layer document");
        }
        const userLayerDocument = parseCalculatorUserOverlayDocumentV4(parseJsonDocument(
          userLayers[0],
          "calculator v4 user layer",
        ));
        const presentations = Array.from(
          this.querySelectorAll?.(CALCULATOR_ENGINE_PRESENTATION_DOCUMENT_SELECTOR) ?? [],
        );
        if (presentations.length !== 1) {
          throw new Error("fishy-calculator-engine requires exactly one calculator presentation document");
        }
        const staticPresentation = parseStaticPresentation(parseJsonDocument(
          presentations[0],
          "calculator static presentation",
        ));
        void this._activateInitialModelDocument(response, {
          userLayerDocument,
          staticPresentation,
        });
      } catch (error) {
        this._setFailure(error, { preserveAsStale: false });
      }
    });
  }

  disconnectedCallback() {
    this._connected = false;
    this._connectionRevision += 1;
    this._activationSequence += 1;
    this._modelRequestLifecycle.reset();
    this._pendingModelRequestId = null;
    this._pendingModelIntentRevision = null;
    this._pendingValidationRequestId = null;
    this._pendingValidationIntentRevision = null;
    this._scenarioIntentTransaction = null;
    const engine = this._engine;
    this._engine = null;
    this._disposeEngine(engine);
  }

  get runtimeLoader() {
    return this._runtimeLoader;
  }

  set runtimeLoader(value) {
    if (typeof value !== "function") {
      throw new TypeError("runtimeLoader must be a function");
    }
    if (value !== this._runtimeLoader) {
      this._runtimeLoader = value;
      this._runtime = null;
      this._runtimePromise = null;
      this._runtimePromiseEngineVersion = null;
    }
  }

  get model() {
    return this._model;
  }

  get modelRevision() {
    return this._model?.modelRevision ?? null;
  }

  get projection() {
    return this._projection;
  }

  get confirmedProjection() {
    return this._confirmedProjection;
  }

  get status() {
    return this._status;
  }

  get pendingModelRequestId() {
    return this._pendingModelRequestId;
  }

  get lastRequestId() {
    return this._lastRequestId;
  }

  get presentation() {
    return this._presentation;
  }

  get outputValidity() {
    return this._outputValidity;
  }

  get hasNonCurrentOutputs() {
    return this._outputValidity.some((entry) => entry.state !== "current");
  }

  get repairReport() {
    return this._repairReport;
  }

  get scenario() {
    return this._scenario;
  }

  get confirmedScenario() {
    return this._confirmedScenario;
  }

  set scenario(value) {
    this.evaluateScenario(value);
  }

  get userLayer() {
    return this._userLayer;
  }

  set userLayer(value) {
    this.setUserLayer(value);
  }

  acceptScenario(scenario, patch) {
    if (this._scenarioIntentTransaction !== null) {
      this._scenarioIntentTransaction.scenario = scenario;
      this._scenarioIntentTransaction.patch = mergeScenarioIntentPatch(
        this._scenarioIntentTransaction.patch,
        patch,
      );
      return;
    }
    this._dispatchScenarioIntent(scenario, patch);
  }

  beginScenarioIntentTransaction() {
    if (this._scenarioIntentTransaction !== null) {
      throw new Error("calculator scenario intent transaction is already active");
    }
    this._scenarioIntentTransaction = { scenario: null, patch: {} };
    return true;
  }

  commitScenarioIntentTransaction() {
    const transaction = this._scenarioIntentTransaction;
    if (transaction === null) {
      throw new Error("calculator scenario intent transaction is not active");
    }
    this._scenarioIntentTransaction = null;
    if (transaction.scenario === null) {
      return false;
    }
    this._dispatchScenarioIntent(transaction.scenario, transaction.patch);
    return true;
  }

  cancelScenarioIntentTransaction() {
    const active = this._scenarioIntentTransaction !== null;
    this._scenarioIntentTransaction = null;
    return active;
  }

  _dispatchScenarioIntent(scenario, patch) {
    this.dispatchEvent?.(eventWithDetail(
      CALCULATOR_ENGINE_SCENARIO_INTENT_EVENT,
      Object.freeze({ scenario, patch }),
    ));
  }

  evaluateScenario(value) {
    const scenario = parseCalculatorEngineScenarioV2(value);
    this._localIntentRevision += 1;
    this._scenario = scenario;
    if (this._pendingModelRequestId !== null) {
      this._scenarioNeedsEvaluation = true;
      return null;
    }
    if (!this._engine) {
      this._scenarioNeedsEvaluation = true;
      return null;
    }

    try {
      const projection = this._resolveProjection(this._engine.evaluate(scenario));
      this._outputValidity = Object.freeze(this._outputValidity.map((entry) => Object.freeze({
        ...entry,
        state: "current",
      })));
      this._scenarioNeedsEvaluation = this._pendingModelRequestId !== null;
      this._projection = projection;
      this._setStatus("local", "Calculated locally with the retained model.");
      this._emitProjection(projection, "local");
      return projection;
    } catch (error) {
      this._setFailure(error, { preserveAsStale: false });
      return null;
    }
  }

  setUserLayer(value) {
    if (value === null) {
      return this.clearUserLayer();
    }
    if (typeof value !== "object" || Array.isArray(value)) {
      throw new TypeError("userLayer must be an object or null");
    }
    const parsedLayer = parseCalculatorUserOverlayDocumentV4({
      documentVersion: 4,
      userLayer: value,
    }).userLayer;
    if (sameJson(parsedLayer, this._userLayer)) {
      return this._projection;
    }
    this._localIntentRevision += 1;
    const operationIndex = this._model
      ? buildCalculatorProjectionOperationIndex(this._model, parsedLayer)
      : null;
    if (!this._engine) {
      this._userLayer = parsedLayer;
      this._operationIndex = operationIndex;
      this._scenarioNeedsEvaluation = true;
      return null;
    }

    try {
      this._engine.setUserLayer(parsedLayer);
    } catch (error) {
      this._setFailure(error, { preserveAsStale: false });
      return null;
    }
    this._userLayer = parsedLayer;
    this._operationIndex = operationIndex;
    return this.evaluateScenario(this._scenario ?? this._model?.defaultScenario);
  }

  clearUserLayer() {
    this._localIntentRevision += 1;
    const operationIndex = this._model
      ? buildCalculatorProjectionOperationIndex(this._model, null)
      : null;
    if (!this._engine) {
      this._userLayer = null;
      this._operationIndex = operationIndex;
      this._scenarioNeedsEvaluation = true;
      return null;
    }
    try {
      this._engine.clearUserLayer();
    } catch (error) {
      this._setFailure(error, { preserveAsStale: false });
      return null;
    }
    this._userLayer = null;
    this._operationIndex = operationIndex;
    return this.evaluateScenario(this._scenario ?? this._model?.defaultScenario);
  }

  applyUserLayerDraft(document, action) {
    if (!this._engine) {
      throw new Error("calculator engine is not ready to prepare a user-layer draft");
    }
    if (typeof this._engine.applyUserLayerDraft !== "function") {
      throw new TypeError("calculator runtime module is missing applyUserLayerDraft");
    }
    return this._engine.applyUserLayerDraft(document, action);
  }

  beginModelRequest(requestId, scenario = null) {
    this._activationSequence += 1;
    this._pendingModelRequestId = requireRequestId(requestId);
    this._pendingModelIntentRevision = this._localIntentRevision;
    if (scenario !== null) {
      this._scenario = parseCalculatorEngineScenarioV2(scenario);
      this._scenarioNeedsEvaluation = false;
    }
    this._pendingValidationRequestId = null;
    this._pendingValidationIntentRevision = null;
    this._setStatus("loading", "Loading facts for the selected calculator model.", {
      requestId: this._pendingModelRequestId,
    });
    return this._pendingModelRequestId;
  }

  requestModel(request) {
    if (
      request === null
      || typeof request !== "object"
      || Array.isArray(request)
    ) {
      throw new TypeError("calculator model request must be an object");
    }
    const requestId = requireRequestId(request.requestId);
    if (requestId !== this._pendingModelRequestId) {
      throw new Error("calculator model request does not match the active request identity");
    }
    const payload = JSON.parse(JSON.stringify(request));
    this.dispatchEvent?.(eventWithDetail(
      CALCULATOR_ENGINE_MODEL_REQUEST_EVENT,
      Object.freeze({
        schemaVersion: 1,
        request: Object.freeze(payload),
      }),
    ));
    return requestId;
  }

  acceptModelRequestLifecycle(detail) {
    return this._modelRequestLifecycle.accept(detail, this._pendingModelRequestId);
  }

  failLocalEvaluation(error) {
    this._setFailure(error, { preserveAsStale: false });
  }

  async applyModelResponse(value, {
    userLayerDocument = null,
    staticPresentation = null,
    sourceReference = null,
    controlRevision = null,
  } = {}) {
    if (!this._pendingModelRequestId) {
      return false;
    }
    if (value?.requestId !== this._pendingModelRequestId) {
      return false;
    }

    let response;
    try {
      response = parseCalculatorEngineModelResponseV2(value);
    } catch (error) {
      this._pendingModelRequestId = null;
      this._pendingModelIntentRevision = null;
      this._setFailure(error, { preserveAsStale: true, markOutputsStale: true });
      return false;
    }
    if (response.outcome === "rejected") {
      this._pendingModelRequestId = null;
      this._pendingModelIntentRevision = null;
      this._outputValidity = invalidatedOutputValidity(
        this._outputValidity,
        response.affectedOutputs,
      );
      const diagnostic = response.issues.map((issue) => issue.message).filter(Boolean).join(" ");
      this._setStatus("stale", [diagnostic,
        "Some current inputs could not be evaluated; only dependent results are marked invalid. Adjust the highlighted inputs or use preset history to undo the change.",
      ].filter(Boolean).join(" "), {
        issues: response.issues,
        requestId: response.requestId,
      });
      return true;
    }

    let candidates;
    try {
      candidates = {
        userLayerDocument: userLayerDocument === null
          ? { documentVersion: 4, userLayer: this._userLayer }
          : userLayerDocument,
        staticPresentation: staticPresentation ?? this._presentation,
        sourceReference,
        controlRevision: parseCalculatorControlRevision(
          controlRevision,
          response.model.modelRevision,
        ),
      };
      parseCalculatorUserOverlayDocumentV4(candidates.userLayerDocument);
      parseStaticPresentation(candidates.staticPresentation);
      if (sourceReference !== null) {
        requireRequestId(sourceReference, "sourceReference");
      }
    } catch (error) {
      this._pendingModelRequestId = null;
      this._pendingModelIntentRevision = null;
      this._setFailure(error, {
        preserveAsStale: true,
        markOutputsStale: true,
        requestId: response.requestId,
      });
      return false;
    }
    candidates.requestIntentRevision = this._pendingModelIntentRevision;
    this._pendingModelRequestId = null;
    this._pendingModelIntentRevision = null;
    return this._activateReadyResponse(response, candidates);
  }

  failModelRequest(requestId, error) {
    if (requestId !== this._pendingModelRequestId) {
      return false;
    }
    this._pendingModelRequestId = null;
    this._pendingModelIntentRevision = null;
    this._setFailure(error, {
      preserveAsStale: true,
      markOutputsStale: true,
      requestId,
    });
    return true;
  }

  beginValidation(requestId) {
    this._pendingValidationRequestId = requireRequestId(requestId);
    this._pendingValidationIntentRevision = this._localIntentRevision;
    this._setStatus("validating", "Validating this calculator result with the server.", {
      requestId: this._pendingValidationRequestId,
    });
    return this._pendingValidationRequestId;
  }

  applyValidationResponse(value, { validatedUserLayer } = {}) {
    if (!this._pendingValidationRequestId) {
      return false;
    }
    if (value?.requestId !== this._pendingValidationRequestId) {
      return false;
    }
    if (this._pendingValidationIntentRevision !== this._localIntentRevision) {
      this._pendingValidationRequestId = null;
      this._pendingValidationIntentRevision = null;
      return false;
    }

    let response;
    try {
      response = parseCalculatorEngineValidationResponseV1(
        value,
        this._model?.engineVersion,
      );
    } catch (error) {
      this._pendingValidationRequestId = null;
      this._pendingValidationIntentRevision = null;
      this._setFailure(error, { preserveAsStale: true });
      return false;
    }
    this._pendingValidationRequestId = null;
    this._pendingValidationIntentRevision = null;

    if (response.outcome === "validated") {
      if (response.modelRevision !== this.modelRevision) {
        this._setStatus("stale", "The validation response targets a different model revision.", {
          issues: [],
          requestId: response.requestId,
        });
        return true;
      }
      let parsedLayer = this._userLayer;
      let operationIndex = this._operationIndex;
      let projection;
      try {
        if (validatedUserLayer !== undefined) {
          parsedLayer = parseCalculatorUserOverlayDocumentV4({
            documentVersion: 4,
            userLayer: validatedUserLayer,
          }).userLayer;
          operationIndex = buildCalculatorProjectionOperationIndex(
            this._model,
            parsedLayer,
          );
        }
        projection = this._resolveProjection(response.projection, {
          model: this._model,
          operationIndex,
        });
        if (!sameJson(parsedLayer, this._userLayer)) {
          this._engine?.setUserLayer(parsedLayer);
        }
      } catch (error) {
        this._setFailure(error, {
          preserveAsStale: true,
          requestId: response.requestId,
        });
        return false;
      }
      if (!sameJson(parsedLayer, this._userLayer)) {
        this._userLayer = parsedLayer;
        this._localIntentRevision += 1;
      }
      this._operationIndex = operationIndex;
      this._confirmedProjection = projection;
      this._projection = projection;
      this._setStatus("validated", "The server confirmed this calculator result.", {
        requestId: response.requestId,
      });
      this._emitProjection(projection, "validated");
      return true;
    }

    if (response.outcome === "stale") {
      if (response.confirmedModelRevision === this.modelRevision) {
        try {
          const confirmed = this._resolveProjection(response.confirmedProjection);
          this._confirmedProjection = confirmed;
          this._projection = confirmed;
          this._emitProjection(confirmed, "stale");
        } catch (error) {
          this._setFailure(error, {
            preserveAsStale: true,
            issues: response.issues,
            requestId: response.requestId,
          });
          return false;
        }
      }
      this._setStatus("stale", "The local model is stale; the last confirmed result remains visible.", {
        issues: response.issues,
        requestId: response.requestId,
      });
      return true;
    }

    const confirmed = response.confirmedProjection;
    if (confirmed && confirmed.modelRevision === this.modelRevision) {
      try {
        const resolved = this._resolveProjection(confirmed);
        this._confirmedProjection = resolved;
        this._projection = resolved;
        this._emitProjection(resolved, "stale");
      } catch (error) {
        this._setFailure(error, {
          preserveAsStale: true,
          issues: response.issues,
          requestId: response.requestId,
        });
        return false;
      }
    }
    this._setFailure(new Error("The server could not validate this calculator result."), {
      preserveAsStale: true,
      issues: response.issues,
      requestId: response.requestId,
    });
    return true;
  }

  failValidation(requestId, error) {
    if (requestId !== this._pendingValidationRequestId) {
      return false;
    }
    if (this._pendingValidationIntentRevision !== this._localIntentRevision) {
      this._pendingValidationRequestId = null;
      this._pendingValidationIntentRevision = null;
      return false;
    }
    this._pendingValidationRequestId = null;
    this._pendingValidationIntentRevision = null;
    this._setFailure(error, { preserveAsStale: true, requestId });
    return true;
  }

  _readEmbeddedInitialModelDocument() {
    const documents = Array.from(
      this.querySelectorAll?.(CALCULATOR_ENGINE_MODEL_DOCUMENT_SELECTOR) ?? [],
    );
    if (documents.length !== 1) {
      throw new Error(
        `fishy-calculator-engine requires exactly one compact V1 model document; found ${documents.length}`,
      );
    }
    const decoded = parseJsonDocument(documents[0], "fishy-calculator-engine model");
    return parseCalculatorEngineInitialModelDocumentV2(decoded);
  }

  async _activateInitialModelDocument(document, {
    userLayerDocument,
    staticPresentation,
  }) {
    const sequence = ++this._activationSequence;
    const activationIntentRevision = this._localIntentRevision;
    const hadQueuedEvaluation = this._scenarioNeedsEvaluation;
    const queuedScenario = this._scenario;
    const queuedUserLayer = this._userLayer;
    this._scenarioNeedsEvaluation = false;

    let candidateUserLayer;
    let candidatePresentation;
    let candidateModel;
    let candidateOperationIndex;
    try {
      candidateUserLayer = parseCalculatorUserOverlayDocumentV4(
        userLayerDocument,
      ).userLayer;
      candidatePresentation = parseStaticPresentation(staticPresentation);
      candidateModel = expandCalculatorCompactModelBundleV1(document.model);
      candidateOperationIndex = buildCalculatorProjectionOperationIndex(
        candidateModel,
        candidateUserLayer,
      );
      assertCalculatorProjectionOperationBinding({
        modelRevision: document.confirmation.modelRevision,
        userLayerContentHash: document.confirmation.userLayerContentHash,
      }, candidateOperationIndex, "$.initialModelDocument.confirmation");
    } catch (error) {
      this._setFailure(error, {
        preserveAsStale: false,
        requestId: document.requestId,
      });
      return false;
    }

    const hasPreloadedIntent = hadQueuedEvaluation || activationIntentRevision > 0;
    const effectiveScenario = hasPreloadedIntent && queuedScenario !== null
      ? queuedScenario
      : document.normalizedScenario;
    const effectiveUserLayer = hasPreloadedIntent
      ? queuedUserLayer
      : candidateUserLayer;
    let effectiveOperationIndex;
    try {
      effectiveOperationIndex = sameJson(effectiveUserLayer, candidateUserLayer)
        ? candidateOperationIndex
        : buildCalculatorProjectionOperationIndex(candidateModel, effectiveUserLayer);
    } catch (error) {
      this._setFailure(error, {
        preserveAsStale: false,
        requestId: document.requestId,
      });
      return false;
    }

    this._model = candidateModel;
    this._wireModel = document.model;
    this._scenario = effectiveScenario;
    this._confirmedScenario = document.normalizedScenario;
    this._userLayer = effectiveUserLayer;
    this._operationIndex = effectiveOperationIndex;
    this._presentation = candidatePresentation;
    this._outputValidity = Object.freeze(document.outputValidity.map(Object.freeze));
    this._repairReport = Object.freeze({
      repairs: Object.freeze(document.repairReport.repairs.map(Object.freeze)),
    });
    if (!this._emitPresentation(candidatePresentation, candidateModel.modelRevision)) {
      this._setFailure(
        new TypeError("calculator presentation was rejected by its presenter contract"),
        { preserveAsStale: false, requestId: document.requestId },
      );
      return false;
    }
    this._setStatus("loading", "Loading the matching calculator Wasm engine.", {
      requestId: document.requestId,
    });

    let candidateEngine = null;
    try {
      const runtime = await this._loadRuntime(candidateModel.engineVersion);
      if (sequence !== this._activationSequence || !this._connected) {
        return false;
      }
      if (typeof runtime.module?.CalculatorEngine !== "function") {
        throw new TypeError("calculator runtime module is missing CalculatorEngine");
      }
      candidateEngine = new runtime.module.CalculatorEngine(document.model);
      if (candidateEngine.modelRevision() !== candidateModel.modelRevision) {
        throw new Error(
          `calculator runtime modelRevision mismatch: expected ${candidateModel.modelRevision}, got ${candidateEngine.modelRevision()}`,
        );
      }

      candidateEngine.setUserLayer(candidateUserLayer);
      const confirmedProjection = this._resolveProjection(
        candidateEngine.evaluate(document.normalizedScenario),
        {
          model: candidateModel,
          operationIndex: candidateOperationIndex,
        },
      );
      if (sequence !== this._activationSequence || !this._connected) {
        this._disposeEngine(candidateEngine);
        return false;
      }

      const hasNewerLocalIntent = (
        this._localIntentRevision !== activationIntentRevision
        || this._scenarioNeedsEvaluation
        || hasPreloadedIntent
      );
      const currentUserLayer = this._userLayer;
      const currentScenario = this._scenario ?? document.normalizedScenario;
      const currentOperationIndex = sameJson(currentUserLayer, candidateUserLayer)
        ? candidateOperationIndex
        : buildCalculatorProjectionOperationIndex(candidateModel, currentUserLayer);
      let activeProjection = confirmedProjection;
      if (hasNewerLocalIntent) {
        if (currentUserLayer === null) {
          candidateEngine.clearUserLayer();
        } else if (!sameJson(currentUserLayer, candidateUserLayer)) {
          candidateEngine.setUserLayer(currentUserLayer);
        }
        activeProjection = this._resolveProjection(
          candidateEngine.evaluate(currentScenario),
          {
            model: candidateModel,
            operationIndex: currentOperationIndex,
          },
        );
      }

      const hasNewerConfirmedProjection = (
        this._confirmedProjection !== null
        && this._confirmedProjection.modelRevision === candidateModel.modelRevision
        && this._status === "validated"
      );
      this._operationIndex = currentOperationIndex;
      this._engine = candidateEngine;
      candidateEngine = null;
      this._scenarioNeedsEvaluation = false;
      if (!hasNewerConfirmedProjection) {
        this._confirmedProjection = confirmedProjection;
      }
      if (hasNewerLocalIntent) {
        this._projection = activeProjection;
        this._setStatus("local", "Calculated locally with the retained model.", {
          requestId: document.requestId,
        });
        this._emitProjection(activeProjection, "local");
      } else if (!hasNewerConfirmedProjection) {
        this._projection = confirmedProjection;
        this._setStatus(
          "validated",
          this._repairReport.repairs.length > 0
            ? "Calculator data was recovered against the current catalog."
            : "The server confirmation and local engine inputs match.",
          { requestId: document.requestId },
        );
        this._emitProjection(confirmedProjection, "validated");
      } else {
        this._setStatus(
          "validated",
          "The server confirmed this calculator result.",
          { requestId: document.requestId },
        );
      }
      return true;
    } catch (error) {
      this._disposeEngine(candidateEngine);
      if (sequence === this._activationSequence && this._connected) {
        this._engine = null;
        this._setFailure(error, {
          preserveAsStale: false,
          requestId: document.requestId,
        });
      }
      return false;
    }
  }

  async _activateReadyResponse(response, {
    userLayerDocument,
    staticPresentation,
    sourceReference = null,
    controlRevision,
    requestIntentRevision = this._localIntentRevision,
  }) {
    const sequence = ++this._activationSequence;
    const evaluateQueuedScenario = this._scenarioNeedsEvaluation;
    const activationIntentRevision = requestIntentRevision;
    this._scenarioNeedsEvaluation = false;
    const previousEngine = this._engine;
    const isInitialActivation = this._confirmedProjection === null;
    let candidateUserLayer;
    let candidatePresentation;
    let candidateModel;
    let candidateScenario;
    let candidateOperationIndex;
    let candidateProjection;
    try {
      candidateUserLayer = parseCalculatorUserOverlayDocumentV4(
        userLayerDocument,
      ).userLayer;
      candidatePresentation = parseStaticPresentation(staticPresentation);
      candidateModel = expandCalculatorCompactModelBundleV1(response.model);
      candidateScenario = evaluateQueuedScenario && this._scenario !== null
        ? this._scenario
        : response.normalizedScenario;
      candidateOperationIndex = buildCalculatorProjectionOperationIndex(
        candidateModel,
        candidateUserLayer,
      );
      candidateProjection = this._resolveProjection(response.projection, {
        model: candidateModel,
        operationIndex: candidateOperationIndex,
      });
    } catch (error) {
      this._setFailure(error, {
        preserveAsStale: !isInitialActivation,
        markOutputsStale: !isInitialActivation,
        requestId: response.requestId,
      });
      return false;
    }
    if (isInitialActivation) {
      this._model = candidateModel;
      this._wireModel = response.model;
      this._scenario = candidateScenario;
      this._userLayer = candidateUserLayer;
      this._operationIndex = candidateOperationIndex;
      this._presentation = candidatePresentation;
      this._confirmedProjection = candidateProjection;
      this._projection = candidateProjection;
      if (!this._emitPresentation(
        candidatePresentation,
        candidateModel.modelRevision,
      )) {
        this._setFailure(
          new TypeError("calculator presentation was rejected by its presenter contract"),
          { preserveAsStale: false, requestId: response.requestId },
        );
        return false;
      }
      this._emitProjection(candidateProjection, "validated");
    }
    this._setStatus("loading", "Loading the matching calculator Wasm engine.", {
      requestId: response.requestId,
    });

    let candidateEngine = null;
    let controlTransaction = null;
    let presentationRevision = null;
    try {
      const runtime = await this._loadRuntime(candidateModel.engineVersion);
      if (sequence !== this._activationSequence || !this._connected) {
        return false;
      }
      if (typeof runtime.module?.CalculatorEngine !== "function") {
        throw new TypeError("calculator runtime module is missing CalculatorEngine");
      }
      candidateEngine = new runtime.module.CalculatorEngine(response.model);
      if (candidateEngine.modelRevision() !== candidateModel.modelRevision) {
        throw new Error(
          `calculator runtime modelRevision mismatch: expected ${candidateModel.modelRevision}, got ${candidateEngine.modelRevision()}`,
        );
      }
      const hasNewerLocalIntent = this._localIntentRevision !== activationIntentRevision;
      const hasQueuedLocalEvaluation = evaluateQueuedScenario || this._scenarioNeedsEvaluation;
      const effectiveScenario = this._scenarioNeedsEvaluation && this._scenario !== null
        ? this._scenario
        : candidateScenario;
      const hasNewerConfirmedProjection = (
        this._confirmedProjection !== candidateProjection
        && this._confirmedProjection?.modelRevision === candidateModel.modelRevision
        && this._status === "validated"
      );
      const effectiveUserLayer = hasNewerLocalIntent
        ? this._userLayer
        : candidateUserLayer;
      const effectiveOperationIndex = sameJson(effectiveUserLayer, candidateUserLayer)
        ? candidateOperationIndex
        : buildCalculatorProjectionOperationIndex(candidateModel, effectiveUserLayer);
      if (effectiveUserLayer === null) {
        candidateEngine.clearUserLayer();
      } else {
        candidateEngine.setUserLayer(effectiveUserLayer);
      }
      // Prove that the replacement runtime can evaluate the exact scenario and
      // user layer that will become active before publishing any revision-bound
      // presentation metadata. A failed candidate must leave the retained
      // model, projection, presenter, and Wasm owner as one coherent unit.
      const candidateLocalProjection = this._resolveProjection(
        candidateEngine.evaluate(effectiveScenario),
        {
          model: candidateModel,
          operationIndex: effectiveOperationIndex,
        },
      );
      if (sequence !== this._activationSequence || !this._connected) {
        this._disposeEngine(candidateEngine);
        return false;
      }
      controlTransaction = prepareCalculatorControlRevision(
        this.ownerDocument,
        controlRevision,
        candidateModel.modelRevision,
      );
      if (!isInitialActivation) {
        presentationRevision = createCalculatorPresentationRevision();
        const accepted = this._emitPresentation(
          candidatePresentation,
          candidateModel.modelRevision,
          presentationRevision.boundary,
        );
        presentationRevision.seal();
        if (!accepted) {
          throw new TypeError("calculator presentation was rejected by its presenter contract");
        }
      }
      const retainedRevision = Object.freeze({
        confirmedProjection: this._confirmedProjection,
        confirmedScenario: this._confirmedScenario,
        engine: this._engine,
        model: this._model,
        operationIndex: this._operationIndex,
        outputValidity: this._outputValidity,
        presentation: this._presentation,
        projection: this._projection,
        scenario: this._scenario,
        scenarioNeedsEvaluation: this._scenarioNeedsEvaluation,
        repairReport: this._repairReport,
        userLayer: this._userLayer,
        wireModel: this._wireModel,
      });
      const candidateRevision = Object.freeze({
        confirmedProjection: !hasNewerConfirmedProjection || hasQueuedLocalEvaluation
          ? candidateProjection
          : this._confirmedProjection,
        confirmedScenario: response.normalizedScenario,
        engine: candidateEngine,
        model: candidateModel,
        operationIndex: effectiveOperationIndex,
        outputValidity: Object.freeze(response.outputValidity.map(Object.freeze)),
        presentation: candidatePresentation,
        projection: !hasNewerConfirmedProjection || hasQueuedLocalEvaluation
          ? (hasQueuedLocalEvaluation ? candidateLocalProjection : candidateProjection)
          : this._projection,
        scenario: effectiveScenario,
        scenarioNeedsEvaluation: false,
        repairReport: Object.freeze({
          repairs: Object.freeze((response.repairReport?.repairs ?? []).map(Object.freeze)),
        }),
        userLayer: effectiveUserLayer,
        wireModel: response.model,
      });
      const applyRevision = (revision) => {
        this._model = revision.model;
        this._wireModel = revision.wireModel;
        this._scenario = revision.scenario;
        this._userLayer = revision.userLayer;
        this._operationIndex = revision.operationIndex;
        this._outputValidity = revision.outputValidity;
        this._presentation = revision.presentation;
        this._confirmedProjection = revision.confirmedProjection;
        this._confirmedScenario = revision.confirmedScenario;
        this._projection = revision.projection;
        this._engine = revision.engine;
        this._scenarioNeedsEvaluation = revision.scenarioNeedsEvaluation;
        this._repairReport = revision.repairReport;
      };
      let publicationState = "prepared";
      const revisionPublication = Object.freeze({
        schemaVersion: 1,
        activate: () => {
          if (publicationState !== "prepared") {
            throw new Error("calculator model revision is not prepared for activation");
          }
          publicationState = "active";
          applyRevision(candidateRevision);
          presentationRevision?.activate();
          this.dispatchEvent?.(eventWithDetail(
            CALCULATOR_ENGINE_MODEL_REVISION_ACTIVATED_EVENT,
            Object.freeze({
              schemaVersion: 1,
              requestId: response.requestId,
              modelRevision: candidateRevision.model.modelRevision,
              phase: "candidate",
            }),
          ));
        },
        finalize: () => {
          if (publicationState !== "active") {
            throw new Error("calculator model revision is not active");
          }
          presentationRevision?.finalize();
          publicationState = "finalized";
        },
        rollback: () => {
          if (publicationState === "rolledBack") {
            return;
          }
          if (publicationState === "finalized") {
            throw new Error("finalized calculator model revision cannot be rolled back");
          }
          publicationState = "rolledBack";
          applyRevision(retainedRevision);
          this.dispatchEvent?.(eventWithDetail(
            CALCULATOR_ENGINE_MODEL_REVISION_ACTIVATED_EVENT,
            Object.freeze({
              schemaVersion: 1,
              requestId: response.requestId,
              modelRevision: retainedRevision.model.modelRevision,
              phase: "rollback",
            }),
          ));
          presentationRevision?.rollback();
        },
      });
      controlTransaction.commit(revisionPublication);
      controlTransaction.finalize();
      controlTransaction = null;
      presentationRevision = null;
      candidateEngine = null;
      if (previousEngine !== this._engine) {
        this._disposeEngine(previousEngine);
      }
      if (sourceReference !== null) {
        this.setAttribute("data-calculator-source-reference", sourceReference);
      }
      if (sourceReference !== null) {
        this.dispatchEvent?.(eventWithDetail(
          CALCULATOR_ENGINE_MODEL_COMMITTED_EVENT,
          Object.freeze({
            schemaVersion: 1,
            requestId: response.requestId,
            modelRevision: candidateModel.modelRevision,
            sourceReference,
            normalizedScenario: this._scenario,
            outcome: response.outcome,
            repairReport: this._repairReport,
            outputValidity: this._outputValidity,
          }),
        ));
      }
      if (
        !isInitialActivation
        && !hasNewerConfirmedProjection
        && !hasQueuedLocalEvaluation
      ) {
        this._emitProjection(candidateProjection, "validated");
      }
      if (hasQueuedLocalEvaluation) {
        this._setStatus("local", "Calculated locally with the accepted model.", {
          requestId: response.requestId,
        });
        this._emitProjection(candidateLocalProjection, "local");
      } else {
        this._setStatus(
          "validated",
          response.outcome === "recovered"
            ? "Calculator data was recovered against the current catalog."
            : "The server projection and local engine revision match.",
          { requestId: response.requestId },
        );
      }
      return true;
    } catch (error) {
      const failures = [error];
      try {
        controlTransaction?.rollback();
      } catch (rollbackError) {
        failures.push(rollbackError);
      }
      try {
        presentationRevision?.rollback();
      } catch (rollbackError) {
        failures.push(rollbackError);
      }
      this._disposeEngine(candidateEngine);
      if (sequence === this._activationSequence && this._connected) {
        if (isInitialActivation) {
          this._engine = null;
        }
        this._setFailure(failures.length === 1 ? error : new AggregateError(
          failures,
          "calculator model revision activation and rollback failed",
        ), {
          preserveAsStale: !isInitialActivation,
          markOutputsStale: !isInitialActivation,
          requestId: response.requestId,
        });
      }
      return false;
    }
  }

  _loadRuntime(expectedEngineVersion) {
    if (this._runtime?.engineVersion === expectedEngineVersion) {
      return Promise.resolve(this._runtime);
    }
    if (
      !this._runtimePromise
      || this._runtimePromiseEngineVersion !== expectedEngineVersion
    ) {
      let checkedPromise;
      const loadPromise = Promise.resolve().then(() => (
        this._runtimeLoader({ expectedEngineVersion })
      ));
      checkedPromise = loadPromise.then((runtime) => {
        if (runtime?.engineVersion !== expectedEngineVersion) {
          throw new Error(
            `calculator runtime engineVersion mismatch: expected ${expectedEngineVersion}, got ${String(runtime?.engineVersion)}`,
          );
        }
        if (this._runtimePromise === checkedPromise) {
          this._runtime = runtime;
        }
        return runtime;
      }).catch((error) => {
        if (this._runtimePromise === checkedPromise) {
          this._runtimePromise = null;
          this._runtimePromiseEngineVersion = null;
        }
        throw error;
      });
      this._runtimePromise = checkedPromise;
      this._runtimePromiseEngineVersion = expectedEngineVersion;
    }
    return this._runtimePromise;
  }

  _emitPresentation(
    presentation,
    modelRevision = this.modelRevision,
    presentationRevision = null,
  ) {
    return this.dispatchEvent?.(eventWithDetail(
      CALCULATOR_ENGINE_PRESENTATION_EVENT,
      Object.freeze({
        schemaVersion: 1,
        modelRevision,
        presentation,
        presentationRevision,
      }),
      { cancelable: true },
    )) !== false;
  }

  _disposeEngine(engine) {
    if (engine && typeof engine.free === "function") {
      engine.free();
    }
  }

  _resolveProjection(projection, {
    model = this._model,
    operationIndex = this._operationIndex,
  } = {}) {
    if (!model || !operationIndex) {
      throw new Error("calculator projection resolution requires an active model operation index");
    }
    const parsed = parseCalculatorInspectorProjectionV1(projection, model);
    return hydrateCalculatorProjectionTraceCandidates(parsed, operationIndex);
  }

  _emitProjection(projection, status) {
    this.dispatchEvent?.(eventWithDetail(
      CALCULATOR_ENGINE_PROJECTION_EVENT,
      Object.freeze({
        schemaVersion: 1,
        status,
        modelRevision: projection.modelRevision,
        projection,
      }),
    ));
  }

  _setFailure(error, {
    preserveAsStale,
    markOutputsStale = false,
    issues = [],
    requestId = null,
  } = {}) {
    const serialized = serializedError(error);
    const canRestoreConfirmed = (
      preserveAsStale
      && this._confirmedProjection
      && this._confirmedProjection.modelRevision === this.modelRevision
    );
    if (canRestoreConfirmed && this._projection !== this._confirmedProjection) {
      this._projection = this._confirmedProjection;
      this._emitProjection(this._confirmedProjection, "stale");
    }
    if (markOutputsStale) {
      this._outputValidity = staleOutputValidity(this._outputValidity);
    }
    const status = canRestoreConfirmed ? "stale" : "failed";
    const message = status === "stale"
      ? `${serialized.message} The last confirmed result remains visible.`
      : serialized.message;
    this._setStatus(status, message, { error: serialized, issues, requestId });
  }

  _setStatus(status, message, {
    error = null,
    issues = [],
    requestId = null,
  } = {}) {
    if (!CALCULATOR_ENGINE_STATUSES.includes(status)) {
      throw new TypeError(`unsupported calculator engine status: ${String(status)}`);
    }
    this._status = status;
    if (requestId !== null) {
      this._lastRequestId = requestId;
    }
    this._statusMessage = message;
    this._statusError = error;
    this._renderStatus();
    this.dispatchEvent?.(eventWithDetail(
      CALCULATOR_ENGINE_STATUS_EVENT,
      Object.freeze({
        schemaVersion: 1,
        status,
        modelRevision: this.modelRevision,
        requestId,
        message,
        error,
        issues: Object.freeze([...issues]),
        outputValidity: this._outputValidity,
        repairReport: this._repairReport,
      }),
    ));
  }

  _renderStatus() {
    this.setAttribute?.("data-calculator-engine-state", this._status);
    this.setAttribute?.("aria-busy", String(
      this._status === "loading" || this._status === "validating",
    ));
    let output = this.querySelector?.("[data-calculator-engine-status]") ?? null;
    if (!output && this.ownerDocument?.createElement && this.appendChild) {
      output = this.ownerDocument.createElement("output");
      output.setAttribute("data-calculator-engine-status", "");
      this.appendChild(output);
    }
    if (output) {
      output.setAttribute("class", "sr-only");
      output.setAttribute("role", "status");
      output.setAttribute("aria-live", "polite");
      output.setAttribute("aria-atomic", "true");
      output.textContent = `Calculator: ${statusLabel(this._status)} — ${this._statusMessage}`;
    }
    const recovery = this.querySelector?.("[data-calculator-recovery-boundary]") ?? null;
    if (recovery && "hidden" in recovery) {
      const hasRepairs = this._repairReport.repairs.length > 0;
      const hasNonCurrent = this._outputValidity.some((entry) => entry.state !== "current");
      recovery.hidden = !hasRepairs && !hasNonCurrent && this._status !== "failed";
      const message = recovery.querySelector?.("[data-calculator-validity-message]");
      if (message) {
        message.textContent = this._statusMessage;
      }
      const summary = recovery.querySelector?.("[data-calculator-repair-summary]");
      if (summary) {
        summary.textContent = this._repairReport.repairs
          .map((repair) => `${repair.path}: ${repair.kind}`)
          .join("; ");
        summary.hidden = !hasRepairs;
      }
      const dismiss = recovery.querySelector?.("[data-calculator-repair-dismiss]");
      if (dismiss) {
        dismiss.hidden = hasNonCurrent || !hasRepairs;
      }
    }
    for (const share of this.querySelectorAll?.("[data-calculator-result-share]") ?? []) {
      const disabled = this._outputValidity.some((entry) => entry.state !== "current");
      share.disabled = disabled;
      share.setAttribute?.("aria-disabled", String(disabled));
      if (disabled) {
        share.setAttribute?.("data-fishy-tooltip", RESULT_SHARE_UNAVAILABLE_TOOLTIP);
      } else {
        share.removeAttribute?.("data-fishy-tooltip");
      }
    }
  }
}

export class FishyCalculatorModelResponse extends HTMLElementBase {
  static get observedAttributes() {
    return ["data-request-id"];
  }

  constructor() {
    super();
    this._observer = null;
    this._consumedDocument = null;
  }

  connectedCallback() {
    void this._consume();
    if (typeof globalThis.MutationObserver === "function") {
      this._observer = new globalThis.MutationObserver(() => {
        void this._consume();
      });
      this._observer.observe(this, {
        attributes: false,
        characterData: true,
        childList: true,
        subtree: true,
      });
    }
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    this._observer = null;
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this._consumedDocument = null;
    }
  }

  async _consume() {
    const documents = Array.from(
      this.querySelectorAll?.(CALCULATOR_ENGINE_MODEL_RESPONSE_DOCUMENT_SELECTOR) ?? [],
    );
    if (documents.length === 0) {
      return;
    }
    const engine = this.ownerDocument?.getElementById?.("calculator-engine") ?? null;
    const document = documents[0];
    if (document === this._consumedDocument || !engine) {
      return;
    }
    this._consumedDocument = document;
    const requestId = this.getAttribute?.("data-request-id");
    try {
      if (documents.length !== 1) {
        throw new Error("calculator model response carrier requires exactly one response document");
      }
      const response = parseCalculatorEngineModelResponseV2(parseJsonDocument(
        document,
        "calculator model response",
      ));
      if (requestId !== response.requestId) {
        throw new Error("calculator model response carrier request ID mismatch");
      }
      if (requestId !== engine.pendingModelRequestId) {
        return;
      }
      const presentations = Array.from(
        this.querySelectorAll?.(CALCULATOR_ENGINE_PRESENTATION_DOCUMENT_SELECTOR) ?? [],
      );
      const userLayers = Array.from(
        this.querySelectorAll?.(CALCULATOR_ENGINE_USER_LAYER_DOCUMENT_SELECTOR) ?? [],
      );
      const sourceReferences = Array.from(
        this.querySelectorAll?.(CALCULATOR_ENGINE_SOURCE_REFERENCE_DOCUMENT_SELECTOR) ?? [],
      );
      const controlRevisions = Array.from(
        this.querySelectorAll?.(CALCULATOR_ENGINE_CONTROL_REVISION_SELECTOR) ?? [],
      );
      if (response.outcome === "ready" || response.outcome === "recovered") {
        if (presentations.length !== 1) {
          throw new Error("accepted calculator model response requires exactly one static presentation document");
        }
        if (userLayers.length !== 1) {
          throw new Error("accepted calculator model response requires exactly one v4 user-layer document");
        }
        if (sourceReferences.length !== 1) {
          throw new Error("accepted calculator model response requires exactly one source-reference document");
        }
        if (controlRevisions.length !== 1) {
          throw new Error("accepted calculator model response requires exactly one control revision");
        }
        await engine.applyModelResponse(response, {
          userLayerDocument: parseJsonDocument(userLayers[0], "calculator v4 user layer"),
          staticPresentation: parseJsonDocument(
            presentations[0],
            "calculator static presentation",
          ),
          sourceReference: parseSourceReference(parseJsonDocument(
            sourceReferences[0],
            "calculator source reference",
          )),
          controlRevision: parseCalculatorControlRevisionTemplate(
            controlRevisions[0],
            response.model.modelRevision,
          ),
        });
        return;
      }
      if (
        presentations.length !== 0
        || userLayers.length !== 0
        || sourceReferences.length !== 0
        || controlRevisions.length !== 0
      ) {
        throw new Error("failed calculator model response must not carry model side documents");
      }
      await engine.applyModelResponse(response);
    } catch (error) {
      engine.failModelRequest(requestId, error);
    }
  }
}

export function registerCalculatorEngine() {
  if (typeof globalThis.customElements === "undefined") {
    return;
  }
  if (!globalThis.customElements.get(CALCULATOR_ENGINE_TAG)) {
    globalThis.customElements.define(CALCULATOR_ENGINE_TAG, FishyCalculatorEngine);
  }
  if (!globalThis.customElements.get(CALCULATOR_ENGINE_MODEL_RESPONSE_TAG)) {
    globalThis.customElements.define(
      CALCULATOR_ENGINE_MODEL_RESPONSE_TAG,
      FishyCalculatorModelResponse,
    );
  }
}
